import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Server, Mail, ExternalLink, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import logoCorge from '../../assets/logos/logo-corgee.png';
import {
  tenantRegistrationService,
  defaultServerUrl,
  normalizeBaseUrl,
  buildFirstSetupUrl,
  extractApiError,
} from '../../services/tenantRegistrationService';
import type { TenantRow } from '../../types/tenantRegistration';

interface TenantAccessPanelProps {
  row: TenantRow;
  onClose: () => void;
  onSaveServer: (tenancyName: string, serverUrl: string) => void;
  onResend: (row: TenantRow, serverUrl: string) => Promise<void>;
}

function formatDate(value: string): string
{
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Login-style side panel opened from the administrator email.
 * Lets the registrar configure the server of the tenant, test it, open the
 * first-time configuration (initial wizard) and resend the access email.
 */
export const TenantAccessPanel: React.FC<TenantAccessPanelProps> = ({
  row,
  onClose,
  onSaveServer,
  onResend,
}) =>
{
  const [serverUrl, setServerUrl] = useState(row.serverUrl || defaultServerUrl());
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<{ ok: boolean; message: string } | null>(
    null,
  );

  useEffect(() =>
  {
    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const effectiveServer = normalizeBaseUrl(serverUrl) || defaultServerUrl();
  const firstSetupUrl = buildFirstSetupUrl(row.tenantUrl);

  const handleTest = useCallback(async () =>
  {
    setTesting(true);
    setTestResult(null);
    try
    {
      const tenant = await tenantRegistrationService.getTenantFromServer(
        effectiveServer,
        row.tenancyName,
      );
      if (tenant && tenant.TenancyName)
      {
        setTestResult({
          ok: true,
          message: `Server responded. Tenant "${tenant.TenancyName}" found${
            tenant.isActive === false ? ' (inactive)' : ''
          }.`,
        });
      }
      else
      {
        setTestResult({ ok: false, message: 'Server responded but the tenant was not found.' });
      }
    }
    catch (error)
    {
      setTestResult({ ok: false, message: extractApiError(error, 'Could not reach the server.') });
    }
    finally
    {
      setTesting(false);
    }
  }, [effectiveServer, row.tenancyName]);

  const handleSave = useCallback(() =>
  {
    onSaveServer(row.tenancyName, effectiveServer);
  }, [onSaveServer, row.tenancyName, effectiveServer]);

  const handleResend = useCallback(async () =>
  {
    setResending(true);
    setResendMessage(null);
    try
    {
      await onResend(row, effectiveServer);
      setResendMessage({ ok: true, message: `Access email sent to ${row.emailAddress}.` });
    }
    catch (error)
    {
      setResendMessage({ ok: false, message: extractApiError(error, 'The email could not be sent.') });
    }
    finally
    {
      setResending(false);
    }
  }, [onResend, row, effectiveServer]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative h-full w-full max-w-md bg-[#ededed] shadow-2xl overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-16 pb-8">
          <div className="bg-white rounded-xl shadow-xl relative pt-16">
            <div className="absolute -top-14 left-1/2 -translate-x-1/2">
              <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                <img src={logoCorge} alt="Corgee Logo" className="w-20 h-20 object-contain" />
              </div>
            </div>

            <div className="p-6">
              <h2 className="text-2xl text-center text-[#62accb] font-normal">
                {row.tenancyName}
              </h2>
              <p className="text-center text-gray-500 text-sm mb-5 break-all">{row.emailAddress}</p>

              {/* Server configuration */}
              <div className="mb-5">
                <label className="flex items-center gap-1.5 mb-1 text-sm font-medium text-gray-700">
                  <Server className="w-4 h-4 text-[#62accb]" /> Server (API URL)
                </label>
                <input
                  type="url"
                  value={serverUrl}
                  onChange={e =>
                  {
                    setServerUrl(e.target.value);
                    setTestResult(null);
                  }}
                  placeholder={defaultServerUrl()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Server used to resend emails and test this tenant. Default comes from .env.
                </p>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={handleTest}
                    disabled={testing}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {testing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Test connection
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-3 py-1.5 rounded-md text-sm bg-[#62accb] hover:bg-[#4a9bd1] text-white"
                  >
                    Save server
                  </button>
                </div>
                {testResult && (
                  <p
                    className={`mt-2 flex items-start gap-1.5 text-xs ${
                      testResult.ok ? 'text-green-700' : 'text-red-600'
                    }`}
                  >
                    {testResult.ok ? (
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 shrink-0" />
                    )}
                    {testResult.message}
                  </p>
                )}
              </div>

              <hr className="border-gray-200 mb-5" />

              {/* Links */}
              <div className="space-y-2 mb-5">
                <a
                  href={row.tenantUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-[#62accb] hover:bg-[#4a9bd1] text-white text-sm rounded-md transition-colors"
                >
                  Open tenant (Start) <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href={firstSetupUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between w-full px-4 py-2.5 border border-[#62accb] text-[#62accb] hover:bg-[#62accb]/10 text-sm rounded-md transition-colors"
                >
                  First-time site configuration <ExternalLink className="w-4 h-4" />
                </a>
                <p className="text-xs text-gray-400">
                  The initial wizard is only shown until the site has been configured. Afterwards
                  this link lands on the regular login.
                </p>
              </div>

              <hr className="border-gray-200 mb-5" />

              {/* Emails */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <Mail className="w-4 h-4 text-[#62accb]" /> Emails sent
                  </h3>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending || !row.emailAddress}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-[#62accb] hover:bg-[#4a9bd1] text-white disabled:opacity-50"
                  >
                    {resending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Resend email
                  </button>
                </div>
                {resendMessage && (
                  <p
                    className={`mb-2 text-xs ${resendMessage.ok ? 'text-green-700' : 'text-red-600'}`}
                  >
                    {resendMessage.message}
                  </p>
                )}
                {row.emails.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    No emails recorded from this console for this tenant.
                  </p>
                ) : (
                  <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                    {[...row.emails].reverse().map(email => (
                      <li
                        key={`${email.kind}-${email.sentAt}-${email.sentTo}`}
                        className="flex items-start gap-2 text-xs text-gray-600 border border-gray-100 rounded-md px-2.5 py-1.5"
                      >
                        {email.ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-gray-700">
                            {email.kind === 'welcome' ? 'Welcome email' : 'Access email'}
                            {!email.ok && ' (failed)'}
                          </div>
                          <div className="break-all">to {email.sentTo}</div>
                          <div className="text-gray-400">{formatDate(email.sentAt)}</div>
                          {email.error && <div className="text-red-500">{email.error}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  The welcome email is sent by the server when the tenant is created. Resending
                  triggers the access (password reset) email for the administrator account.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-gray-400">
            Corgee © CGBOT {new Date().getFullYear()}
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
};
